import { useCallback, useEffect, useState, MouseEvent } from 'react';
import { ImageModel, ThumbnailModel } from '../types';

export const IMAGE_REFETCH_INTERVAL = 5000;

export function hasUnsettledImages(imageData?: ImageModel[]): boolean {
    if (!imageData) {
        return false;
    }

    let isStillScaling = (thumbnail: ThumbnailModel) => thumbnail.reference === null;
    let isStillUploading = (image: ImageModel) => image.uploadsInProgress.length > 0;

    return imageData.some(
        (image) => isStillUploading(image) || image.thumbnails.some(isStillScaling)
    );
}

export function createSelectedThumbnailModel(files: ThumbnailModel[]): Map<number, boolean> {
    return files.reduce((acc, file) => {
        acc.set(file.id, false);
        return acc;
    }, new Map<number, boolean>());
}

export function transformSelectedImagesToListOfIds(selectedImages: Map<number, boolean>): number[] {
    return Array.from(selectedImages.entries())
        .filter(([, isSelected]) => isSelected)
        .map(([id]) => id);
}

export function useSelectThumbnailClickHandler(
    files: ThumbnailModel[],
    onClick?: (event, id) => void
) {
    let initialSelection = createSelectedThumbnailModel(files);
    let [selectedImages, setSelectedImages] = useState<Map<number, boolean>>(initialSelection);

    function selectAll() {
        let newSelection = new Map(selectedImages);
        files.forEach((file) => {
            newSelection.set(file.id, true);
        });
        setSelectedImages(newSelection);
    }

    function deselectAll() {
        let newSelection = new Map(selectedImages);
        files.forEach((file) => {
            newSelection.set(file.id, false);
        });
        setSelectedImages(newSelection);
    }

    function handleClick(event: MouseEvent<HTMLDivElement>, id: number) {
        if (event.ctrlKey) {
            let currentSelection = selectedImages.get(id);
            selectedImages.set(id, !currentSelection);
            setSelectedImages(new Map(selectedImages));
        } else {
            if (typeof onClick === 'function') {
                onClick(event, id);
            }
        }
    }

    return {
        selectedImages,
        handleClick,
        selectAll: useCallback(selectAll, [selectedImages, files]),
        deselectAll: useCallback(deselectAll, [selectedImages, files])
    };
}

export function shouldDisableDeleteMultipleImagesButton(
    existingImages: ThumbnailModel[],
    selectedImages: Map<number, boolean>,
    hasUploadInProgress: boolean
): boolean {
    if (existingImages.length === 0) {
        return true;
    }

    if (hasUploadInProgress) {
        return true;
    }

    let numberOfSelectedThumbnails = Array.from(selectedImages.values()).filter(Boolean).length;

    if (numberOfSelectedThumbnails < 2) {
        return numberOfSelectedThumbnails !== selectedImages.size;
    }

    return false;
}

export function useDisableDeleteMultipleImages(
    existingImages: ThumbnailModel[],
    selectedImages: Map<number, boolean>,
    enableActions: boolean
) {
    let [disableDeleteMultiple, setDisableDeleteMultiple] = useState(false);

    useEffect(() => {
        let shouldDisable = shouldDisableDeleteMultipleImagesButton(
            existingImages,
            selectedImages,
            !enableActions
        );

        if (shouldDisable !== disableDeleteMultiple) {
            setDisableDeleteMultiple(shouldDisable);
        }
    }, [selectedImages, existingImages, enableActions]);

    return disableDeleteMultiple;
}
